import * as React from "react";
import { useQuery } from "react-apollo";
import { RouteStore } from "bernie-core";
import { BlossomCustomerNotification, blossomCustomerNotificationQuery, ContextInput } from "__generated__/api";
import Customer = BlossomCustomerNotification.Customer;
import Links = BlossomCustomerNotification.Links;
import Actions = BlossomCustomerNotification.Actions;
import { gql } from "apollo-boost";
import { Link } from "react-router-dom";
import { UitkCard } from "uitk-react-cards";
import {
  UitkMessagingCard,
  UitkMessagingCardMedia,
  UitkMessagingCardSection,
  UitkMessagingCardImageLayout,
} from "uitk-react-messaging-cards";
import { inject, observer } from "mobx-react";
import { UitkIcon, UitkIconSize } from "uitk-react-icons";
import { NotificationDialog } from "./NotificationDialog";
import { UitkLink } from "uitk-react-link";
import { UitkText } from "uitk-react-text";
import { UitkLayoutFlex } from "uitk-react-layout-flex";
import { sendAnalytics, getLineOfBusiness, getFunnelLocation } from "./NotificationQueryUtils";
import { ClientChatbotStore } from "src/stores/chatbotStore/ClientChatbotStore";
import {
  StorefrontWizardRegionTypesContext,
  StorefrontWizardRegionTypes,
} from "components/flexComponents/PageLevelProviders/WizardContextProvider/StorefrontWizardRegionTypesContext";
import { NotificationBannerBEX } from "./NotificationBannerBEX";

export interface CustomerNotificationWithDialogProps {
  inputs: {
    context: ContextInput;
    lineOfBusiness: string;
    funnelLocation: string;
    location: string;
  };
  analyticsCallback: (rfrr: string, linkName: string | null) => void;
  className?: string;
  chatbot?: ClientChatbotStore;
  routeStore?: RouteStore;
}

const queryCustomerNotifications = gql`
  ${blossomCustomerNotificationQuery}
`;

export const CustomerNotificationWithDialog = inject("routeStore")(
  observer((props: CustomerNotificationWithDialogProps) => {
    const queryVariables = {
      context: props.inputs.context,
      notificationLocation: props.inputs.location,
      lineOfBusiness: getLineOfBusiness(props.inputs.lineOfBusiness),
      funnelLocation: getFunnelLocation(props.inputs.funnelLocation),
    };
    const { routeStore, analyticsCallback, className = "" } = props;
    const regionTypeContext = React.useContext(StorefrontWizardRegionTypesContext);
    const bexWizard = regionTypeContext === StorefrontWizardRegionTypes.STOREFRONT_WIZARD_REGION_BEX;

    const { data, error, loading } = useQuery<{ customer: Customer }>(queryCustomerNotifications, {
      variables: queryVariables,
    });

    if (error || loading || !data || !data?.customer.notification || !data.customer.notification.body) {
      return null;
    }

    const { title, body, links } = data.customer.notification;

    const heading = title?.items[0].text;
    const message = body?.[0]?.items[0].text;

    const bannerLinks = links?.map(({ text, url, icon, actions }: Links) => ({ text, href: url, icon, actions }));
    const firstLink = bannerLinks?.shift();

    const dialogId = "covid-19-dialog";
    const dialogQuery = routeStore?.mergeQueryString(routeStore.search, {
      pwaDialog: "covid-19-dialog",
    });

    const onClickLink = (e: React.MouseEvent<HTMLElement>) => {
      sendAnalytics(firstLink!.actions as Actions[], analyticsCallback);
    };

    return (
      <div
        data-testid="customer-notification-with-dialog"
        data-stid="customer-notifications"
        className={className}
        role="banner"
      >
        <NotificationDialog
          dialogId={dialogId}
          inputs={{
            context: props.inputs.context,
            lineOfBusiness: props.inputs.lineOfBusiness,
            funnelLocation: props.inputs.funnelLocation,
          }}
          analyticsCallback={props.analyticsCallback}
        />
        {bexWizard ? (
          <NotificationBannerBEX
            heading={heading}
            message={message!}
            linkText={firstLink?.text}
            dialogQuery={dialogQuery}
            onClickEvent={onClickLink}
          />
        ) : (
          <UitkCard withBorder={true}>
            <UitkMessagingCard
              imageLayout={UitkMessagingCardImageLayout.LEFT}
              className={"all-y-padding-three all-x-padding-three travel-advisory-with-chatbot-link"}
            >
              <UitkMessagingCardMedia withBackground={true}>
                <UitkIcon name="help" size={UitkIconSize.MEDIUM} />
              </UitkMessagingCardMedia>
              <UitkMessagingCardSection className={"all-l-padding-three"} heading={heading} headingLevel="h4">
                <p>
                  <UitkText size={300}>{message}</UitkText>
                </p>
                <UitkLayoutFlex className={"all-t-padding-two"} space={"six"}>
                  <UitkLink onClick={onClickLink}>
                    <Link to={{ search: dialogQuery }}>{firstLink?.text}</Link>
                  </UitkLink>
                </UitkLayoutFlex>
              </UitkMessagingCardSection>
            </UitkMessagingCard>
          </UitkCard>
        )}
      </div>
    );
  })
);
